import { useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import type { WorkbenchView } from "./analysis-workbench";

/** The search params of a workbench route. */
export const WORKBENCH_SEARCH = z.object({
  view: z.enum(["queue", "results", "withdrawn"]).optional(),
  /** The open request: its sample number. */
  request: z.coerce.number().int().positive().optional(),
  report: z.coerce.number().int().positive().optional(),
  /** The in-house session being worked through. */
  session: z.coerce.number().int().positive().optional(),
});

type WorkbenchSearch = z.infer<typeof WORKBENCH_SEARCH>;

/**
 * The open tube, the open report and the session being worked through live in
 * the URL, so a number or a session can be linked to from the overview (or
 * pasted to a colleague) and opens in place.
 */
export function useWorkbenchRoute() {
  // Any workbench route (an analysis, a method) carries the same search
  // params: see WORKBENCH_SEARCH.
  const search = useSearch({ strict: false }) as WorkbenchSearch;
  const navigate = useNavigate();
  const update = (patch: {
    view?: WorkbenchView;
    request?: number | null;
    report?: number | null;
    session?: number | null;
  }) =>
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        ...("view" in patch
          ? { view: patch.view === "results" ? undefined : patch.view }
          : {}),
        ...("request" in patch ? { request: patch.request ?? undefined } : {}),
        ...("report" in patch ? { report: patch.report ?? undefined } : {}),
        ...("session" in patch ? { session: patch.session ?? undefined } : {}),
      }),
      replace: true,
    });

  return {
    // Results is the landing view; the queue is a click (or a link) away.
    view: search.view ?? ("results" as WorkbenchView),
    requestId: search.request ?? null,
    reportId: search.report ?? null,
    /** The session being worked through: the queue narrows to its samples. */
    sessionId: search.session ?? null,
    setView: (view: WorkbenchView) => update({ view }),
    selectRequest: (id: number | null) => update({ request: id }),
    selectReport: (id: number | null) => update({ report: id }),
    /** Work through a session, optionally opening one of its samples. */
    focusSession: (session: number | null, request: number | null = null) =>
      update({ view: "queue", session, request }),
    /** Open a request in the queue (e.g. one just restored). */
    showRequest: (id: number) =>
      update({ view: "queue", request: id, report: null, session: null }),
    /** A result was just saved: show it in the results half. */
    openResult: (reportId: number) =>
      update({ view: "results", report: reportId, request: null }),
  };
}
